import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import LiteralsService from 'src/services/literals/LiteralsService';
import styles from './secondaryNavigation.module.scss';
import dlsContentStyles from 'src/styles/dlsContentStyles.module.scss';
import { BudgetPages } from 'src/components/atomic/pages/BudgetPages';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { BudgetSecondaryNavLink } from 'src/types/budget-secondary-nav-link.enum';
import { trackClick } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import { useSecondaryNavSelectedTab } from 'src/hooks/useSecondaryNavSelectedTab';
import Selectors from 'src/utils/selectors';
import { DLSBreakpoints } from 'src/types/dlsBreakpoints.enum';
import { isMobileApp } from 'src/utils/url.utils';

export default function SecondaryNavigation(): JSX.Element {
    const history = useHistory();
    const isOnMobileApp = isMobileApp();
    const { isCustomMinWidth: isMinWidthDLSMedium } = useBreakpoints(DLSBreakpoints.MEDIUM);
    const { selectedTab, selectedTabName } = useSecondaryNavSelectedTab();
    const faqURL = process.env.REACT_APP_FAQ;

    /** Hides/shows the dropdown menu when the navigation dropdown is clicked. */
    const [showDropdownMenu, setShowDropdownMenu] = useState<boolean>(false);

    /** Hides the menu of the navigation dropdown if the user expands the screen. Although it will already
     *  technically be hidden if the screen width increases, if the user decreases the width again, they will see
     *  the menu shown if we do not hide it before. */
    useEffect(() => {
        if (isMinWidthDLSMedium && showDropdownMenu) {
            setShowDropdownMenu(false);
        }
    }, [isMinWidthDLSMedium]);

    // This uses the DLS breakpoint to match the global header, didn't add enum as this should be only use
    return isMinWidthDLSMedium ? (
        <nav
            className={`nav border nav-horizontal ${styles.secondaryNav}`}
            data-toggle="nav"
            data-responsive="true"
            aria-label={LiteralsService.getLiteral('SecondaryNavigation.secondaryNav')}
            id="secondaryNav"
        >
            <ul className={`nav-menu ${styles.secondaryNavList}`} data-cy={Selectors.SpendSmart.SECONDARY_NAVIGATION}>
                <li className="nav-item">
                    <Link
                        className="nav-link"
                        onClick={() => trackClick(history.location.pathname, adobeTags.SecondaryNav.Spendsmart)}
                        aria-current={selectedTab === BudgetSecondaryNavLink.SPENDSMART ? 'page' : 'false'}
                        to={BudgetPages.HOME}
                        data-cy={Selectors.General.NAV_LINK}
                    >
                        {LiteralsService.getLiteral('SecondaryNavigation.spendSmart')}
                    </Link>
                </li>
                <li className="nav-item">
                    <Link
                        className="nav-link"
                        onClick={() => trackClick(history.location.pathname, adobeTags.SecondaryNav.Transactions)}
                        aria-current={selectedTab === BudgetSecondaryNavLink.TRANSACTIONS ? 'page' : 'false'}
                        to={BudgetPages.TRANSACTIONS}
                        data-cy={Selectors.General.NAV_LINK}
                    >
                        {LiteralsService.getLiteral('SecondaryNavigation.transactions')}
                    </Link>
                </li>
                <li className="nav-item">
                    <Link
                        className="nav-link"
                        onClick={() => trackClick(history.location.pathname, adobeTags.SecondaryNav.Settings)}
                        aria-current={selectedTab === BudgetSecondaryNavLink.SETTINGS ? 'page' : 'false'}
                        to={BudgetPages.SETTINGS}
                        data-cy={Selectors.General.NAV_LINK}
                    >
                        {LiteralsService.getLiteral('SecondaryNavigation.settings')}
                    </Link>
                </li>
                <li className="nav-item">
                    <a
                        className="nav-link flex"
                        onClick={() => trackClick(history.location.pathname, adobeTags.SecondaryNav.Faqs)}
                        target={isOnMobileApp ? '_self' : '_blank'}
                        rel="noopener noreferrer"
                        href={faqURL}
                        data-cy={Selectors.General.NAV_LINK}
                    >
                        {LiteralsService.getLiteral('SecondaryNavigation.FAQs')}
                    </a>
                </li>
            </ul>
        </nav>
    ) : (
        <nav
            className={`nav nav-vertical border ${styles.dropdownSecondaryNav}`}
            data-toggle="nav"
            data-responsive="true"
            id="secondaryNav"
            aria-label={LiteralsService.getLiteral('SecondaryNavigation.secondaryNav')}
        >
            <div className={`${dlsContentStyles.dlsView} margin-1-tb`}>
                <button
                    onClick={() => setShowDropdownMenu(!showDropdownMenu)}
                    className={`nav-dropdown flex ${styles.dropdownSecondaryNavButton}`}
                    aria-haspopup="true"
                    aria-expanded="false"
                    aria-label="Click to open secondary navigation panel"
                    data-cy={Selectors.SpendSmart.SECONDARY_NAVIGATION_DROPDOWN_BUTTON}
                >
                    {selectedTabName}
                </button>
            </div>
            <ul
                className={`nav-menu border-t ${showDropdownMenu ? '' : 'hidden'} ${styles.dropdownSecondaryNavList}`}
                data-cy={Selectors.SpendSmart.SECONDARY_NAVIGATION_DROPDOWN}
            >
                <li className="nav-item">
                    <Link
                        className="nav-link flex"
                        onClick={() => {
                            setShowDropdownMenu(false);
                            trackClick(history.location.pathname, adobeTags.SecondaryNav.Spendsmart);
                        }}
                        aria-current={selectedTab === BudgetSecondaryNavLink.SPENDSMART ? 'page' : 'false'}
                        to={BudgetPages.HOME}
                        data-cy={Selectors.General.NAV_LINK}
                    >
                        {LiteralsService.getLiteral('SecondaryNavigation.spendSmart')}
                    </Link>
                </li>
                <li className="nav-item">
                    <Link
                        className="nav-link flex"
                        onClick={() => {
                            setShowDropdownMenu(false);
                            trackClick(history.location.pathname, adobeTags.SecondaryNav.Transactions);
                        }}
                        aria-current={selectedTab === BudgetSecondaryNavLink.TRANSACTIONS ? 'page' : 'false'}
                        to={BudgetPages.TRANSACTIONS}
                        data-cy={Selectors.General.NAV_LINK}
                    >
                        {LiteralsService.getLiteral('SecondaryNavigation.transactions')}
                    </Link>
                </li>
                <li className="nav-item">
                    <Link
                        className="nav-link flex"
                        aria-current={selectedTab === BudgetSecondaryNavLink.SETTINGS ? 'page' : 'false'}
                        onClick={() => {
                            setShowDropdownMenu(false);
                            trackClick(history.location.pathname, adobeTags.SecondaryNav.Settings);
                        }}
                        to={BudgetPages.SETTINGS}
                        data-cy={Selectors.General.NAV_LINK}
                        role="button"
                    >
                        {LiteralsService.getLiteral('SecondaryNavigation.settings')}
                    </Link>
                </li>
                <li className="nav-item">
                    <a
                        className="nav-link flex"
                        target={isOnMobileApp ? '_self' : '_blank'}
                        rel="noopener noreferrer"
                        href={faqURL}
                        data-cy={Selectors.General.NAV_LINK}
                        onClick={() => {
                            setShowDropdownMenu(false);
                            trackClick(history.location.pathname, adobeTags.SecondaryNav.Faqs);
                        }}
                    >
                        {LiteralsService.getLiteral('SecondaryNavigation.FAQs')}
                    </a>
                </li>
            </ul>
            {showDropdownMenu && <div onClick={() => setShowDropdownMenu(false)} className={styles.overlay} />}
        </nav>
    );
}
